import React, { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { openPdf } from "@/lib/pdf";
import { toast } from "sonner";
import { Ticket, Users, Home, Trash2, Plus, CheckCircle2, XCircle, Clock, Loader2, Printer, FileText } from "lucide-react";

const TIER_META = {
  solo:    { label: "Solo",    color: "#D4AF37", icon: Ticket },
  duo:     { label: "Duo",     color: "#EEDC82", icon: Users },
  familie: { label: "Familie", color: "#8FBC8F", icon: Home  },
};

const fmt = (iso) => { try { return new Date(iso).toLocaleDateString("de-DE"); } catch { return iso || "—"; } };

export default function Abos() {
  const [subs, setSubs] = useState([]);
  const [plans, setPlans] = useState([]);
  const [busy, setBusy] = useState(false);
  const [grant, setGrant] = useState({ open: false, guest: null, tier: "solo", months: 12 });
  const [guests, setGuests] = useState([]);
  const [q, setQ] = useState("");

  const load = async () => {
    const [{ data: s }, { data: p }] = await Promise.all([
      api.get("/admin/subscriptions"),
      api.get("/subscriptions/plans"),
    ]);
    setSubs(s); setPlans(p.plans || []);
  };
  useEffect(() => { load(); }, []);

  const searchGuests = async (v) => {
    setQ(v);
    if (!v || v.length < 2) { setGuests([]); return; }
    try {
      const { data } = await api.get(`/guests?q=${encodeURIComponent(v)}`);
      setGuests((data || []).slice(0, 8));
    } catch { setGuests([]); }
  };

  const doGrant = async () => {
    if (!grant.guest) return;
    setBusy(true);
    try {
      await api.post("/admin/subscriptions/grant", { guest_id: grant.guest.id, tier: grant.tier, months: parseInt(grant.months) || 12 });
      toast.success(`${TIER_META[grant.tier].label}-Abo für ${grant.guest.first_name || grant.guest.email} aktiviert`);
      setGrant({ open: false, guest: null, tier: "solo", months: 12 });
      setQ(""); setGuests([]);
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Fehler"); }
    finally { setBusy(false); }
  };

  const revoke = async (s) => {
    if (!window.confirm(`Abo von ${s.guest_name} wirklich stornieren?`)) return;
    try {
      await api.delete(`/admin/subscriptions/${s.guest_id}`);
      toast.success("Storniert");
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Fehler"); }
  };

  const active = subs.filter((s) => s.status === "active");
  const cancelled = subs.filter((s) => s.status !== "active");

  return (
    <div className="space-y-6" data-testid="admin-abos-page">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <div className="text-[10px] uppercase tracking-[0.3em] text-[#A8A398]">Movie House Club</div>
          <h1 className="font-heading text-2xl sm:text-3xl md:text-4xl">Abos & Monatskarten</h1>
          <div className="text-xs text-[#A8A398] mt-1">Verwalte laufende Monatskarten oder aktiviere ein Abo direkt an der Kasse.</div>
        </div>
        <button onClick={() => setGrant({ ...grant, open: true })} data-testid="abo-grant-open"
          className="inline-flex items-center gap-2 bg-[#D4AF37] hover:bg-[#EEDC82] text-black px-5 py-2.5 rounded text-[11px] uppercase tracking-widest">
          <Plus size={14} /> Abo an Kasse aktivieren
        </button>
      </div>

      {/* Plan-Übersicht */}
      <div className="grid md:grid-cols-3 gap-3">
        {plans.map((p) => {
          const meta = TIER_META[p.tier] || TIER_META.solo;
          const Icon = meta.icon;
          const count = active.filter((s) => s.tier === p.tier).length;
          return (
            <div key={p.tier} className="card-lux p-5 flex items-start gap-4" data-testid={`abo-plan-${p.tier}`}>
              <div className="w-11 h-11 rounded-full flex items-center justify-center" style={{ background: `${meta.color}18`, color: meta.color }}>
                <Icon size={20} />
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-[10px] uppercase tracking-widest" style={{ color: meta.color }}>{p.label}</div>
                <div className="font-heading text-2xl">{p.price_monthly.toFixed(0)} €<span className="text-sm text-[#A8A398]"> / Monat</span></div>
                <div className="text-[10px] text-[#575148] mt-1">Aktive Abonnenten</div>
                <div className="font-mono text-lg text-[#F5F0E6]">{count}</div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Aktive Abos */}
      <div className="card-lux p-0 overflow-hidden">
        <div className="px-5 py-3 border-b border-[#1A1A1A] flex items-center justify-between">
          <div className="text-[10px] uppercase tracking-[0.32em] text-[#D4AF37]">Aktive Abos · {active.length}</div>
        </div>
        {active.length === 0 ? (
          <div className="p-8 text-center text-[#575148] text-sm">Noch keine aktiven Abos.</div>
        ) : (
          <div className="divide-y divide-[#151515]">
            {active.map((s) => {
              const meta = TIER_META[s.tier] || TIER_META.solo;
              const Icon = meta.icon;
              return (
                <div key={s.guest_id} className="px-5 py-3 flex items-center gap-4" data-testid={`abo-row-${s.guest_id}`}>
                  <div className="w-9 h-9 rounded-full flex items-center justify-center shrink-0" style={{ background: `${meta.color}18`, color: meta.color }}>
                    <Icon size={16} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm truncate">{s.guest_name || s.email || "—"}</div>
                    <div className="text-[10px] text-[#575148] mt-0.5">{s.email} · Nr. {s.member_number || "—"}</div>
                  </div>
                  <div className="text-[10px] uppercase tracking-widest" style={{ color: meta.color }}>{meta.label}</div>
                  <div className="hidden md:block text-[10px] text-[#A8A398]">
                    {s.source === "online" ? "Stripe" : "Kasse"}
                  </div>
                  <div className="hidden md:block text-[10px] text-[#A8A398]">
                    <Clock size={10} className="inline mr-1" /> bis {fmt(s.expires_at)}
                  </div>
                  {s.cancel_at_period_end && (
                    <div className="text-[10px] uppercase tracking-widest text-[#EE9CA1]">gekündigt</div>
                  )}
                  <a href={`${process.env.REACT_APP_BACKEND_URL}/api/guests/${s.guest_id}/abo-card/pdf`} target="_blank" rel="noreferrer"
                    onClick={(e) => { e.preventDefault(); openPdf(`/guests/${s.guest_id}/abo-card/pdf`); }}
                    data-testid={`abo-pdf-${s.guest_id}`}
                    className="text-[#8FBC8F] p-1.5 hover:bg-[#8FBC8F]/10 rounded" title="Abo-Karte als PDF öffnen">
                    <FileText size={14} />
                  </a>
                  <button onClick={async () => {
                      try { await api.post(`/guests/${s.guest_id}/abo-card/print-office`); toast.success("Abo-Karte an Bürodrucker gesendet"); }
                      catch (e) {
                        toast.info("Bürodrucker nicht erreichbar — PDF wird geöffnet");
                        openPdf(`/guests/${s.guest_id}/abo-card/pdf`);
                      }
                    }}
                    data-testid={`abo-print-${s.guest_id}`}
                    className="text-[#D4AF37] p-1.5 hover:bg-[#D4AF37]/10 rounded" title="Am Bürodrucker drucken (öffnet PDF wenn Drucker offline)">
                    <Printer size={14} />
                  </button>
                  <button onClick={() => revoke(s)} data-testid={`abo-revoke-${s.guest_id}`}
                    className="text-[#EE9CA1] p-1.5 hover:bg-[#EE9CA1]/10 rounded" title="Sofort stornieren">
                    <Trash2 size={14} />
                  </button>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Beendet / Storniert */}
      {cancelled.length > 0 && (
        <div className="card-lux p-0 overflow-hidden">
          <div className="px-5 py-3 border-b border-[#1A1A1A] text-[10px] uppercase tracking-[0.32em] text-[#575148]">Beendet · {cancelled.length}</div>
          <div className="divide-y divide-[#151515]">
            {cancelled.map((s) => (
              <div key={s.guest_id} className="px-5 py-2.5 flex items-center gap-4 text-[#575148]">
                <XCircle size={14} className="text-[#EE9CA1] shrink-0" />
                <div className="flex-1 min-w-0 text-sm truncate">{s.guest_name || s.email}</div>
                <div className="text-[10px] uppercase tracking-widest">{TIER_META[s.tier]?.label || s.tier}</div>
                <div className="text-[10px]">bis {fmt(s.expires_at)}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Grant Modal */}
      {grant.open && (
        <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4" data-testid="abo-grant-modal">
          <div className="card-lux p-6 max-w-lg w-full space-y-4">
            <div className="flex items-start justify-between">
              <div>
                <div className="text-[10px] uppercase tracking-widest text-[#D4AF37]">Kassen-Aktivierung</div>
                <div className="font-heading text-2xl">Abo für Kunden aktivieren</div>
              </div>
              <button onClick={() => setGrant({ open: false, guest: null, tier: "solo", months: 12 })}
                className="text-[#575148] hover:text-[#F5F0E6] text-xl">×</button>
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-widest text-[#A8A398]">Kunde suchen (Name / E-Mail / Kundennummer)</label>
              <input value={q} onChange={(e) => searchGuests(e.target.value)} placeholder="Anfangen zu tippen …"
                data-testid="abo-grant-search"
                className="w-full mt-1 bg-[#050505] border border-[#2A2A2A] rounded px-3 py-2 outline-none focus:border-[#D4AF37]" />
              {guests.length > 0 && !grant.guest && (
                <div className="mt-2 border border-[#1A1A1A] rounded max-h-56 overflow-y-auto">
                  {guests.map((g) => (
                    <button key={g.id} onClick={() => setGrant({ ...grant, guest: g })}
                      data-testid={`abo-grant-guest-${g.id}`}
                      className="w-full text-left px-3 py-2 text-sm hover:bg-[#D4AF37]/10 border-b border-[#1A1A1A]">
                      {g.first_name} {g.last_name} <span className="text-[#575148] text-xs">· {g.email} · {g.member_number || "—"}</span>
                    </button>
                  ))}
                </div>
              )}
              {grant.guest && (
                <div className="mt-2 flex items-center gap-2 text-sm">
                  <CheckCircle2 size={14} className="text-[#8FBC8F]" />
                  <b>{grant.guest.first_name} {grant.guest.last_name}</b>
                  <span className="text-[#575148] text-xs">{grant.guest.email}</span>
                  <button onClick={() => setGrant({ ...grant, guest: null })} className="text-[#EE9CA1] text-xs ml-2">×</button>
                </div>
              )}
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
              {plans.map((p) => {
                const active = grant.tier === p.tier;
                const meta = TIER_META[p.tier] || TIER_META.solo;
                return (
                  <button key={p.tier} onClick={() => setGrant({ ...grant, tier: p.tier })}
                    data-testid={`abo-grant-tier-${p.tier}`}
                    className={`p-3 rounded border text-left transitions-safe ${active ? "border-[#D4AF37] bg-[#D4AF37]/10" : "border-[#2A2A2A] hover:border-[#575148]"}`}>
                    <div className="text-[10px] uppercase tracking-widest" style={{ color: meta.color }}>{p.label}</div>
                    <div className="font-heading text-xl">{p.price_monthly.toFixed(0)} €</div>
                    <div className="text-[10px] text-[#575148]">{p.seats_included} Platz · /Mon</div>
                  </button>
                );
              })}
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-widest text-[#A8A398]">Laufzeit (Monate)</label>
              <input type="number" min="1" max="24" value={grant.months} onChange={(e) => setGrant({ ...grant, months: e.target.value })}
                data-testid="abo-grant-months"
                className="w-full mt-1 bg-[#050505] border border-[#2A2A2A] rounded px-3 py-2 outline-none focus:border-[#D4AF37]" />
              <div className="text-[10px] text-[#575148] mt-1">Standard 12 Monate. Läuft danach automatisch aus.</div>
            </div>
            <button onClick={doGrant} disabled={!grant.guest || busy}
              data-testid="abo-grant-submit"
              className="w-full bg-[#D4AF37] hover:bg-[#EEDC82] text-black py-3 rounded text-[11px] uppercase tracking-widest disabled:opacity-40 inline-flex items-center justify-center gap-2">
              {busy ? <><Loader2 size={14} className="animate-spin" /> Aktiviere …</> : <>Aktivieren</>}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
